import type { StumbleType } from "../data/model";
import { chaser as C, stumble as S } from "../data/tuning";

export type StumbleState = "normal" | "stumbling" | "stumblingToDeath" | "killed" | "invincible";
export type HitResult = "stumble" | "death" | "ignored";

/**
 * The original's stumble rules: a stumble opens a window (5 s) in which the cop is right behind you; a second
 * stumble inside the window is a catch (after a 0.2 s delay); a hard hit is a catch at once. Invincibility
 * (shields, boards, the seconds after a continue) ignores hits.
 */
export class StumbleBehaviour {
  state: StumbleState = "normal";
  windowLeft = 0;
  stumbles = 0;
  escapes = 0;
  private deathLeft = 0;
  /** the run's opening window (SetInitialStumble): when it closes nobody "escaped" */
  private opening = false;
  private invincibleLeft = 0;

  get stumbling(): boolean {
    return this.state === "stumbling" || this.state === "stumblingToDeath";
  }

  get invincible(): boolean {
    return this.invincibleLeft > 0;
  }

  /** Returns "escaped" when a stumble window closes, "death" when the death delay runs out. */
  update(dt: number): "escaped" | "death" | null {
    if (this.invincibleLeft > 0) this.invincibleLeft = Math.max(0, this.invincibleLeft - dt);
    if (this.state === "stumbling") {
      this.windowLeft = Math.max(0, this.windowLeft - dt);
      if (this.windowLeft === 0) {
        this.state = "normal";
        if (this.opening) { this.opening = false; return null; }
        this.escapes += 1;
        return "escaped";
      }
    } else if (this.state === "stumblingToDeath") {
      this.deathLeft = Math.max(0, this.deathLeft - dt);
      if (this.deathLeft === 0) {
        this.state = "killed";
        return "death";
      }
    }
    return null;
  }

  /** Every stumble type stumbles the same way (in the original the type only picks the sound and effect). */
  hit(type: StumbleType): HitResult {
    void type;
    if (this.state === "killed" || this.state === "stumblingToDeath") return "ignored";
    if (this.invincibleLeft > 0) return "ignored";
    if (this.state === "stumbling") {
      this.state = "stumblingToDeath";
      this.deathLeft = S.deathDelay;
      return "death";
    }
    this.state = "stumbling";
    this.windowLeft = S.seconds;
    this.stumbles += 1;
    return "stumble";
  }

  /** Every run starts inside a stumble window: the cop right behind you for the first 5 s (SetInitialStumble). */
  setInitialStumble(): void {
    if (this.invincibleLeft > 0) return;
    this.state = "stumbling";
    this.windowLeft = S.seconds;
    this.opening = true;
  }

  /** The stumble is over at once (a flight starts, the springs, a board): the cop drops back. */
  recover(): void {
    if (this.state !== "stumbling" && this.state !== "stumblingToDeath") return;
    this.opening = false;
    this.state = "normal";
    this.windowLeft = 0;
    this.deathLeft = 0;
  }

  /** A saved crash (shield, board): the hit never happened, but you are briefly untouchable. */
  absorb(seconds: number): void {
    this.setInvincible(seconds);
  }

  setInvincible(seconds: number): void {
    this.invincibleLeft = Math.max(this.invincibleLeft, seconds);
  }

  revive(): void {
    this.state = "normal";
    this.windowLeft = 0;
    this.deathLeft = 0;
    this.invincibleLeft = S.postReviveInvincibleSeconds;
  }
}

export interface ChaserPose {
  /** metres behind the car */
  distance: number;
  x: number;
  y: number;
  /** 0 = the normal gap, 1 = right on your tail */
  closeness: number;
  visible: boolean;
}

/** ELON (the chaser: you stole his car): follows at the normal gap, closes in for a stumble window, drops back when you escape or fly. */
export class Chaser {
  distance: number = C.minDistance;
  x = 0;
  y = 0;
  private introLeft: number = C.introSeconds;
  private flyLeft = 0;

  update(dt: number, stumbling: boolean, carX: number, carY: number, flying: boolean, tutorial: boolean): void {
    if (this.introLeft > 0) this.introLeft = Math.max(0, this.introLeft - dt);
    if (flying) this.flyLeft = 1.5; else if (this.flyLeft > 0) this.flyLeft = Math.max(0, this.flyLeft - dt);
    let target = C.maxDistance;
    if (stumbling || this.introLeft > 0) target = C.minDistance;
    if (tutorial) target = C.tutorialFallback;
    if (this.flyLeft > 0) target = C.maxDistance * 3;
    const span = C.maxDistance - C.minDistance;
    if (target < this.distance) this.distance = Math.max(target, this.distance - span / C.catchupSeconds * dt);
    else this.distance = Math.min(target, this.distance + span / C.resetSeconds * dt);
    this.x += (carX - this.x) * Math.min(1, dt / C.laneSmoothSeconds);
    this.y += (carY * C.jumpFactor - this.y) * Math.min(1, dt / C.heightSmoothSeconds);
  }

  /** After a continue he is pushed back to the normal gap at once. */
  pushBack(): void {
    this.distance = C.maxDistance;
    this.introLeft = 0;
    this.flyLeft = 0;
  }

  pose(): ChaserPose {
    const closeness = Math.min(1, Math.max(0, (C.maxDistance - this.distance) / (C.maxDistance - C.minDistance)));
    return { distance: this.distance, x: this.x, y: this.y, closeness, visible: this.distance < C.maxDistance * 2.5 };
  }
}
